import { useSyncExternalStore } from "react";
import { API_BASE } from "../api/base";
import { resolveImageUrl } from "../config/SiteConfigContext";
import { cleanTitle, isSafeSlug } from "./ids";
import { safeColor } from "./tokens";

/**
 * Catalogo per le card della richiesta: le confezioni (con foto e minimo
 * d'ordine) raggruppate per collezione.
 *
 * - Si carica SOLO quando serve (scelta di un intento con confezioni, contesto
 *   di una confezione), mai al montaggio del widget presente su ogni pagina.
 * - Una cache per lingua, condivisa fra pagina e widget, con una sola richiesta
 *   in volo; «Riprova» la svuota e ricarica.
 * - È un arricchimento: con una risposta inattesa (nei test ogni fetch risponde
 *   "{}"), un errore o un timeout lo stato diventa `error` e il modulo funziona
 *   lo stesso (le confezioni si scrivono nella nota).
 */

export interface CatalogBox {
  slug: string;
  title: string;
  imageUrl: string;
  moq: number | null;
  collection: string | null;
}

export interface CatalogGroup {
  slug: string | null;   // null = «Altre confezioni»
  title: string | null;
  color: string | null;  // theme_color della collezione: solo decorativo
  boxes: CatalogBox[];
}

export interface Catalog {
  groups: CatalogGroup[];
  bySlug: Record<string, CatalogBox>;
}

type Entry = { status: "loading" } | { status: "ready"; data: Catalog } | { status: "error" };
export type CatalogStatus = "idle" | Entry["status"];

const TIMEOUT_MS = 8000;
const cache = new Map<string, Entry>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function asBoxes(raw: unknown): CatalogBox[] | null {
  if (!Array.isArray(raw)) return null;
  const out: CatalogBox[] = [];
  for (const p of raw) {
    if (!p || typeof p !== "object") continue;
    const r = p as Record<string, unknown>;
    if (!isSafeSlug(r.slug)) continue;
    const title = cleanTitle(r.title);
    if (!title) continue;
    if (r.is_active === false) continue;
    const moq = typeof r.moq === "number" && Number.isInteger(r.moq) && r.moq > 0 ? r.moq : null;
    const region = isSafeSlug(r.region) ? r.region : null;
    out.push({
      slug: r.slug,
      title,
      imageUrl: resolveImageUrl(typeof r.image_url === "string" ? r.image_url : ""),
      moq,
      collection: region,
    });
  }
  return out;
}

interface CollectionInfo { slug: string; title: string; color: string | null }

function asCollections(raw: unknown): CollectionInfo[] {
  if (!Array.isArray(raw)) return [];
  const out: CollectionInfo[] = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const r = c as Record<string, unknown>;
    if (!isSafeSlug(r.slug)) continue;
    const title = cleanTitle(r.title);
    if (!title) continue;
    out.push({ slug: r.slug, title, color: safeColor(r.theme_color) });
  }
  return out;
}

/** Raggruppa per collezione nell'ordine dell'API; dentro il gruppo per titolo. */
export function buildCatalog(products: CatalogBox[], collections: CollectionInfo[], lang: string): Catalog {
  const byTitle = (a: CatalogBox, b: CatalogBox) => a.title.localeCompare(b.title, lang);
  const groups: CatalogGroup[] = [];
  const placed = new Set<string>();
  for (const c of collections) {
    const boxes = products.filter((p) => p.collection === c.slug).sort(byTitle);
    if (!boxes.length) continue;
    boxes.forEach((b) => placed.add(b.slug));
    groups.push({ slug: c.slug, title: c.title, color: c.color, boxes });
  }
  const rest = products.filter((p) => !placed.has(p.slug)).sort(byTitle);
  if (rest.length) groups.push({ slug: null, title: null, color: null, boxes: rest });
  // Senza prototipo: nessuno slug può leggere proprietà ereditate.
  const bySlug: Record<string, CatalogBox> = Object.create(null);
  for (const p of products) bySlug[p.slug] = p;
  return { groups, bySlug };
}

async function getJson(url: string, signal: AbortSignal): Promise<unknown> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`catalog_${res.status}`);
  return res.json();
}

/**
 * Avvia il caricamento per una lingua, una volta sola. Dopo un errore non
 * riparte da solo (eviterebbe richieste a ogni passaggio del mouse): si
 * ricarica con «Riprova» (`retryCatalog`).
 */
export function loadCatalog(lang: string): void {
  if (cache.has(lang)) return;
  cache.set(lang, { status: "loading" });
  emit();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  const q = `lang=${encodeURIComponent(lang)}`;
  Promise.all([
    getJson(`${API_BASE}/api/v1/shop/products?${q}`, ctrl.signal),
    // Le collezioni servono solo per i titoli dei gruppi: se mancano, elenco piatto.
    getJson(`${API_BASE}/api/v1/shop/collections?${q}`, ctrl.signal).catch(() => []),
  ])
    .then(([products, collections]) => {
      const boxes = asBoxes(products);
      if (!boxes || boxes.length === 0) throw new Error("catalog_shape");
      cache.set(lang, { status: "ready", data: buildCatalog(boxes, asCollections(collections), lang) });
    })
    .catch(() => {
      cache.set(lang, { status: "error" });
    })
    .finally(() => {
      clearTimeout(timer);
      emit();
    });
}

/** «Riprova»: dimentica l'errore e ricarica. */
export function retryCatalog(lang: string): void {
  if (cache.get(lang)?.status === "error") cache.delete(lang);
  loadCatalog(lang);
}

export function useCatalog(lang: string): { status: CatalogStatus; data: Catalog | null } {
  const entry = useSyncExternalStore(subscribe, () => cache.get(lang), () => undefined);
  if (!entry) return { status: "idle", data: null };
  return { status: entry.status, data: entry.status === "ready" ? entry.data : null };
}

/** Solo per i test: ogni test parte senza catalogo in memoria. */
export function __resetCatalogCache(): void {
  cache.clear();
  emit();
}
